import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../components/AdminLayout';
import { adminAPI } from '../services/api';
import { toast } from 'react-toastify';
import { formatAmount } from '../utils/currency';
import {
  FiCreditCard,
  FiUsers,
  FiTrendingUp,
  FiDollarSign,
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiSearch,
  FiArrowUp,
  FiArrowDown,
  FiMinus,
  FiClock,
  FiUser,
  FiEdit3,
  FiAlertCircle,
  FiCheckCircle,
  FiXCircle,
  FiRefreshCw,
} from 'react-icons/fi';

/**
 * AdminSubscriptions - Subscription Governance UI
 * Enterprise Rule: Read-only metrics and history - no direct DB edits
 * Shows: Subscription distribution, user history, audit trail, MRR metrics
 */
const AdminSubscriptions = () => {
  const [stats, setStats] = useState(null);
  const [intelligenceMetrics, setIntelligenceMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userHistory, setUserHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeView, setActiveView] = useState('overview'); // overview | users | detail

  // Fetch statistics and metrics
  const fetchStats = useCallback(async () => {
    try {
      const [statsRes, metricsRes] = await Promise.all([
        adminAPI.getStatistics(),
        adminAPI.getIntelligenceMetrics().catch(() => ({ data: { data: { metrics: null } } })),
      ]);

      setStats(statsRes.data.data.statistics);
      setIntelligenceMetrics(metricsRes.data.data.metrics);
    } catch (error) {
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch users list
  const fetchUsers = useCallback(async () => {
    try {
      const res = await adminAPI.listUsers({
        page,
        limit: 20,
        search: searchQuery || undefined,
      });
      setUsers(res.data.data.users || []);
      setTotal(res.data.data.total || 0);
    } catch (error) {
      toast.error('Failed to load users');
    }
  }, [page, searchQuery]);

  // Fetch user subscription history from audit logs
  const fetchUserHistory = useCallback(async (userId) => {
    setHistoryLoading(true);
    try {
      // Get audit logs filtered by user's subscription changes
      const res = await adminAPI.getAuditLogs({
        entity_type: 'user',
        entity_id: userId,
        limit: 50,
      });

      // Filter only subscription-related actions
      const subscriptionLogs = (res.data.data.logs || []).filter(log =>
        log.action?.includes('plan') ||
        log.action?.includes('subscription') ||
        log.action?.includes('activate') ||
        log.action?.includes('suspend')
      );

      setUserHistory(subscriptionLogs);
    } catch (error) {
      console.error('Failed to fetch user history:', error);
      setUserHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (activeView === 'users') {
      fetchUsers();
    }
  }, [activeView, fetchUsers]);

  useEffect(() => {
    if (selectedUser) {
      fetchUserHistory(selectedUser.id);
    }
  }, [selectedUser, fetchUserHistory]);

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setActiveView('detail');
  };

  const handleBack = () => {
    setSelectedUser(null);
    setUserHistory([]);
    setActiveView('users');
  };

  const totalPages = Math.ceil(total / 20);

  // Calculate tenure (days since signup)
  const calculateTenure = (createdAt) => {
    const days = Math.floor((new Date() - new Date(createdAt)) / (1000 * 60 * 60 * 24));
    if (days < 30) return `${days} days`;
    if (days < 365) return `${Math.floor(days / 30)} months`;
    return `${Math.floor(days / 365)}y ${Math.floor((days % 365) / 30)}m`;
  };

  // Get plan price for MRR contribution
  const getPlanPrice = (planName) => {
    const prices = {
      basic: 0,
      pro: 199,
      premium: 499,
    };
    return prices[planName?.toLowerCase()] || 0;
  };

  // Format timestamp
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get change type icon and color
  const getChangeInfo = (action) => {
    if (action?.includes('change_user_plan')) {
      return { icon: FiEdit3, color: '#FFA800', label: 'Plan Changed' };
    }
    if (action?.includes('activate')) {
      return { icon: FiCheckCircle, color: '#1BC5BD', label: 'Activated' };
    }
    if (action?.includes('suspend')) {
      return { icon: FiXCircle, color: '#F64E60', label: 'Suspended' };
    }
    return { icon: FiClock, color: '#7E8299', label: 'Updated' };
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-loading">
          <div className="admin-loading-spinner"></div>
          <p>Loading subscription data...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">
          <FiCreditCard size={24} style={{ marginRight: 8 }} />
          Subscription Governance
        </h1>
        <p className="admin-page-subtitle">
          Read-only subscription metrics, history, and audit trail
        </p>
      </div>

      {/* View Tabs */}
      <div className="admin-tabs" style={{ marginBottom: 20 }}>
        <button
          className={`admin-tab ${activeView === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveView('overview')}
        >
          <FiTrendingUp size={16} style={{ marginRight: 6 }} />
          Overview
        </button>
        <button
          className={`admin-tab ${activeView === 'users' ? 'active' : ''}`}
          onClick={() => setActiveView('users')}
        >
          <FiUsers size={16} style={{ marginRight: 6 }} />
          Subscribers
        </button>
        {selectedUser && (
          <button
            className={`admin-tab ${activeView === 'detail' ? 'active' : ''}`}
            onClick={() => setActiveView('detail')}
          >
            <FiUser size={16} style={{ marginRight: 6 }} />
            User Detail
          </button>
        )}
      </div>

      {/* Overview Tab */}
      {activeView === 'overview' && (
        <>
          {/* MRR & Key Metrics */}
          <div className="admin-card">
            <div className="admin-card-header">
              <h2 className="admin-card-title">
                <FiDollarSign size={20} />
                Revenue Metrics
              </h2>
              <span className="admin-card-badge">Source: subscription_metrics</span>
            </div>

            <div className="admin-stats-grid">
              <div className="admin-stat-card" style={{ borderLeft: '4px solid #6C5CE7' }}>
                <span className="admin-stat-label">Monthly Recurring Revenue</span>
                {intelligenceMetrics?.current ? (
                  <span className="admin-stat-value" style={{ color: '#6C5CE7' }}>
                    {formatAmount(intelligenceMetrics.current.mrr)}
                  </span>
                ) : (
                  <span className="admin-stat-unavailable">Not available yet</span>
                )}
              </div>

              <div className="admin-stat-card" style={{ borderLeft: '4px solid #1BC5BD' }}>
                <span className="admin-stat-label">Annual Recurring Revenue</span>
                {intelligenceMetrics?.current ? (
                  <span className="admin-stat-value" style={{ color: '#1BC5BD' }}>
                    {formatAmount(intelligenceMetrics.current.arr)}
                  </span>
                ) : (
                  <span className="admin-stat-unavailable">Not available yet</span>
                )}
              </div>

              <div className="admin-stat-card" style={{ borderLeft: '4px solid #3699FF' }}>
                <span className="admin-stat-label">Avg Revenue Per User</span>
                {intelligenceMetrics?.current ? (
                  <span className="admin-stat-value" style={{ color: '#3699FF' }}>
                    {formatAmount(intelligenceMetrics.current.arpu)}
                  </span>
                ) : (
                  <span className="admin-stat-unavailable">Not available yet</span>
                )}
              </div>

              <div className="admin-stat-card" style={{ borderLeft: '4px solid #FFA800' }}>
                <span className="admin-stat-label">Churn Rate (30d)</span>
                {intelligenceMetrics?.current ? (
                  <span className="admin-stat-value" style={{
                    color: intelligenceMetrics.current.churnRate > 5 ? '#F64E60' : '#7E8299'
                  }}>
                    {intelligenceMetrics.current.churnRate.toFixed(1)}%
                  </span>
                ) : (
                  <span className="admin-stat-unavailable">Not available yet</span>
                )}
              </div>
            </div>
          </div>

          {/* Plan Distribution */}
          <div className="admin-card">
            <div className="admin-card-header">
              <h2 className="admin-card-title">
                <FiUsers size={20} />
                Plan Distribution
              </h2>
              <span className="admin-card-badge">Source: user_subscriptions</span>
            </div>

            {stats?.planDistribution && stats.planDistribution.length > 0 ? (
              <div className="subscription-distribution">
                {stats.planDistribution.map((plan, idx) => {
                  const price = getPlanPrice(plan.name);
                  const mrrContribution = price * parseInt(plan.user_count);

                  return (
                    <div key={idx} className="subscription-plan-row">
                      <div className="subscription-plan-info">
                        <span className="subscription-plan-name">{plan.display_name || plan.name}</span>
                        <span className={`subscription-plan-type ${plan.name === 'basic' ? 'free' : 'paid'}`}>
                          {plan.name === 'basic' ? 'Free' : `${formatAmount(price)}/mo`}
                        </span>
                      </div>
                      <div className="subscription-plan-stats">
                        <div className="subscription-stat">
                          <span className="subscription-stat-value">{plan.user_count}</span>
                          <span className="subscription-stat-label">users</span>
                        </div>
                        {price > 0 && (
                          <div className="subscription-stat">
                            <span className="subscription-stat-value">{formatAmount(mrrContribution)}</span>
                            <span className="subscription-stat-label">MRR</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="admin-unavailable">
                <FiAlertCircle size={24} />
                <p>Plan distribution data not available</p>
              </div>
            )}
          </div>

          {/* Conversion & Activity */}
          {intelligenceMetrics?.current && (
            <div className="admin-card">
              <div className="admin-card-header">
                <h2 className="admin-card-title">
                  <FiTrendingUp size={20} />
                  Subscription Activity
                </h2>
                <span className="admin-card-badge">Today</span>
              </div>

              <div className="admin-stats-grid">
                <div className="subscription-activity-card">
                  <FiArrowUp size={24} color="#1BC5BD" />
                  <div>
                    <span className="activity-value">{intelligenceMetrics.current.upgrades}</span>
                    <span className="activity-label">Upgrades</span>
                  </div>
                </div>
                <div className="subscription-activity-card">
                  <FiArrowDown size={24} color="#F64E60" />
                  <div>
                    <span className="activity-value">{intelligenceMetrics.current.downgrades}</span>
                    <span className="activity-label">Downgrades</span>
                  </div>
                </div>
                <div className="subscription-activity-card">
                  <FiMinus size={24} color="#7E8299" />
                  <div>
                    <span className="activity-value">{intelligenceMetrics.current.churned}</span>
                    <span className="activity-label">Churned</span>
                  </div>
                </div>
                <div className="subscription-activity-card">
                  <FiUsers size={24} color="#3699FF" />
                  <div>
                    <span className="activity-value">{intelligenceMetrics.current.newSignups}</span>
                    <span className="activity-label">New Signups</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Subscribers List Tab */}
      {activeView === 'users' && (
        <div className="admin-card">
          <div className="admin-card-header">
            <h2 className="admin-card-title">
              <FiUsers size={20} />
              All Subscribers
            </h2>
            <span className="admin-card-badge">{total} total</span>
          </div>

          <div className="admin-search-wrapper">
            <FiSearch size={18} className="admin-search-icon" />
            <input
              type="text"
              className="admin-search-input"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            />
          </div>

          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Tenure</th>
                  <th>MRR Contribution</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: 40, color: '#7E8299' }}>
                      No subscribers found
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr key={u.id}>
                      <td>
                        <div className="admin-user-cell">
                          <span className="admin-user-name">{u.full_name || 'No name'}</span>
                          <span className="admin-user-email">{u.email}</span>
                        </div>
                      </td>
                      <td>
                        <span className="admin-plan-badge">
                          {u.plan_display || u.plan_name || 'Basic'}
                        </span>
                      </td>
                      <td>
                        <span className={`admin-status ${u.subscription_status === 'active' ? 'active' : 'inactive'}`}>
                          {u.subscription_status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{calculateTenure(u.created_at)}</td>
                      <td>
                        <span style={{ color: getPlanPrice(u.plan_name) > 0 ? '#1BC5BD' : '#7E8299' }}>
                          {formatAmount(getPlanPrice(u.plan_name))}/mo
                        </span>
                      </td>
                      <td>
                        <button
                          className="admin-btn admin-btn-secondary"
                          onClick={() => handleSelectUser(u)}
                        >
                          View History
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="admin-pagination">
              <button
                className="admin-btn admin-btn-secondary"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                <FiChevronLeft size={16} />
                Previous
              </button>
              <span className="admin-pagination-info">
                Page {page} of {totalPages}
              </span>
              <button
                className="admin-btn admin-btn-secondary"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
                <FiChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* User Detail Tab */}
      {activeView === 'detail' && selectedUser && (
        <>
          <button
            onClick={handleBack}
            className="admin-btn admin-btn-secondary"
            style={{ marginBottom: 16 }}
          >
            <FiChevronLeft size={16} />
            Back to Subscribers
          </button>

          {/* User Summary */}
          <div className="admin-card">
            <div className="admin-card-header">
              <h2 className="admin-card-title">
                <FiUser size={20} />
                Subscriber Profile
              </h2>
              <span className="admin-card-badge">Read-only</span>
            </div>

            <div className="user-detail-grid">
              <div className="user-detail-item">
                <span className="user-detail-label">Name</span>
                <span className="user-detail-value">{selectedUser.full_name || 'Not provided'}</span>
              </div>
              <div className="user-detail-item">
                <span className="user-detail-label">Email</span>
                <span className="user-detail-value">{selectedUser.email}</span>
              </div>
              <div className="user-detail-item">
                <span className="user-detail-label">Current Plan</span>
                <span className="user-detail-value">
                  <span className="admin-plan-badge">
                    {selectedUser.plan_display || selectedUser.plan_name || 'Basic'}
                  </span>
                </span>
              </div>
              <div className="user-detail-item">
                <span className="user-detail-label">Status</span>
                <span className={`admin-status ${selectedUser.subscription_status === 'active' ? 'active' : 'inactive'}`}>
                  {selectedUser.subscription_status === 'active' ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="user-detail-item">
                <span className="user-detail-label">Member Since</span>
                <span className="user-detail-value">{formatDate(selectedUser.created_at)}</span>
              </div>
              <div className="user-detail-item">
                <span className="user-detail-label">Tenure</span>
                <span className="user-detail-value">{calculateTenure(selectedUser.created_at)}</span>
              </div>
              <div className="user-detail-item">
                <span className="user-detail-label">MRR Contribution</span>
                <span className="user-detail-value" style={{ color: '#1BC5BD' }}>
                  {formatAmount(getPlanPrice(selectedUser.plan_name))}/mo
                </span>
              </div>
              <div className="user-detail-item">
                <span className="user-detail-label">Last Active</span>
                <span className="user-detail-value">
                  {selectedUser.last_active_at
                    ? formatDate(selectedUser.last_active_at)
                    : selectedUser.last_login
                      ? formatDate(selectedUser.last_login)
                      : 'Never'}
                </span>
              </div>
            </div>
          </div>

          {/* Subscription History (from Audit Logs) */}
          <div className="admin-card">
            <div className="admin-card-header">
              <h2 className="admin-card-title">
                <FiClock size={20} />
                Subscription History
              </h2>
              <span className="admin-card-badge">From audit logs</span>
            </div>

            {historyLoading ? (
              <div className="admin-loading" style={{ padding: 40 }}>
                <FiRefreshCw className="spin" size={24} />
                <p>Loading history...</p>
              </div>
            ) : userHistory.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#7E8299' }}>
                <FiClock size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
                <p>No subscription changes recorded</p>
                <p style={{ fontSize: '0.85rem' }}>Changes will appear here when plans are modified</p>
              </div>
            ) : (
              <div className="subscription-history-timeline">
                {userHistory.map((log, index) => {
                  const changeInfo = getChangeInfo(log.action);
                  const ChangeIcon = changeInfo.icon;

                  return (
                    <div key={log.id || index} className="history-item">
                      <div className="history-marker" style={{ backgroundColor: changeInfo.color }}>
                        <ChangeIcon size={14} color="white" />
                      </div>
                      <div className="history-content">
                        <div className="history-header">
                          <span className="history-action" style={{ color: changeInfo.color }}>
                            {changeInfo.label}
                          </span>
                          <span className="history-date">{formatDate(log.created_at)}</span>
                        </div>

                        {log.old_values?.plan && log.new_values?.plan && (
                          <div className="history-change">
                            <span className="history-old">{log.old_values.plan}</span>
                            <FiArrowRight size={14} />
                            <span className="history-new">{log.new_values.plan}</span>
                          </div>
                        )}

                        <div className="history-admin">
                          <FiUser size={12} />
                          <span>By: {log.admin_email || 'System'}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* Inline Styles */}
      <style>{`
        .subscription-distribution {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .subscription-plan-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
          background: #F8F9FA;
          border-radius: 8px;
        }
        .subscription-plan-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .subscription-plan-name {
          font-weight: 600;
          color: #181C32;
          font-size: 1rem;
        }
        .subscription-plan-type {
          font-size: 0.75rem;
          padding: 4px 8px;
          border-radius: 4px;
        }
        .subscription-plan-type.free {
          background: #E4E6EF;
          color: #7E8299;
        }
        .subscription-plan-type.paid {
          background: #E8FFF3;
          color: #1BC5BD;
        }
        .subscription-plan-stats {
          display: flex;
          gap: 24px;
        }
        .subscription-stat {
          text-align: right;
        }
        .subscription-stat-value {
          display: block;
          font-size: 1.25rem;
          font-weight: 600;
          color: #181C32;
        }
        .subscription-stat-label {
          font-size: 0.75rem;
          color: #7E8299;
        }
        .subscription-activity-card {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 20px;
          background: #F8F9FA;
          border-radius: 8px;
        }
        .activity-value {
          display: block;
          font-size: 1.5rem;
          font-weight: 600;
          color: #181C32;
        }
        .activity-label {
          font-size: 0.85rem;
          color: #7E8299;
        }
        .user-detail-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
        }
        .user-detail-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .user-detail-label {
          font-size: 0.8rem;
          color: #7E8299;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .user-detail-value {
          font-size: 1rem;
          color: #181C32;
          font-weight: 500;
        }
        .subscription-history-timeline {
          position: relative;
          padding-left: 30px;
        }
        .subscription-history-timeline::before {
          content: '';
          position: absolute;
          left: 11px;
          top: 0;
          bottom: 0;
          width: 2px;
          background: #E4E6EF;
        }
        .history-item {
          position: relative;
          padding-bottom: 20px;
        }
        .history-item:last-child {
          padding-bottom: 0;
        }
        .history-marker {
          position: absolute;
          left: -30px;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .history-content {
          background: #F8F9FA;
          border-radius: 8px;
          padding: 12px 16px;
          margin-left: 8px;
        }
        .history-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }
        .history-action {
          font-weight: 600;
        }
        .history-date {
          font-size: 0.8rem;
          color: #B5B5C3;
        }
        .history-change {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
          font-size: 0.9rem;
        }
        .history-old {
          color: #F64E60;
          text-decoration: line-through;
        }
        .history-new {
          color: #1BC5BD;
          font-weight: 500;
        }
        .history-admin {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.8rem;
          color: #7E8299;
        }
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </AdminLayout>
  );
};

// Need to import FiArrowRight
const FiArrowRight = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"></line>
    <polyline points="12 5 19 12 12 19"></polyline>
  </svg>
);

export default AdminSubscriptions;
